import React, { useState, useEffect } from 'react';

/**
 * AdminControls — Emergency controls + platform status + treasury sweep.
 *
 * Features:
 *   - Emergency shutdown / resume toggle
 *   - Platform status indicator
 *   - House wallet balances (on-chain)
 *   - Treasury sweep: AUTO mode (scheduled) / MANUAL mode (button)
 */
const AdminControls = () => {
    const [status, setStatus] = useState(null);
    const [wallets, setWallets] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shutdownReason, setShutdownReason] = useState('');
    const [message, setMessage] = useState('');
    const [treasury, setTreasury] = useState(null);
    const [sweepLoading, setSweepLoading] = useState(false);
    const [sweepMsg, setSweepMsg] = useState('');

    useEffect(() => {
        fetchStatus();
        fetchWallets();
        fetchTreasury();
        const interval = setInterval(() => { fetchStatus(); fetchTreasury(); }, 10000);
        return () => clearInterval(interval);
    }, []);

    const apiFetch = async (path, opts = {}) => {
        const res = await fetch(`/api/admin${path}`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            ...opts,
        });
        return res.json();
    };

    const fetchStatus = async () => {
        try { setStatus(await apiFetch('/status')); } catch { /* ignore */ }
    };

    const fetchWallets = async () => {
        try {
            const data = await apiFetch('/wallets');
            setWallets(data.wallets);
        } catch { /* ignore */ }
    };

    const fetchTreasury = async () => {
        try { setTreasury(await apiFetch('/treasury/status')); } catch { /* ignore */ }
    };

    const handleShutdown = async () => {
        if (!window.confirm('⚠️ EMERGENCY SHUTDOWN — This will stop all new bets. Continue?')) return;
        setLoading(true);
        const data = await apiFetch('/shutdown', {
            method: 'POST',
            body: JSON.stringify({ reason: shutdownReason || 'Manual admin shutdown' }),
        });
        setMessage(data.message || 'Platform paused');
        setShutdownReason('');
        await fetchStatus();
        setLoading(false);
    };

    const handleResume = async () => {
        setLoading(true);
        const data = await apiFetch('/resume', { method: 'POST' });
        setMessage(`Platform resumed (down ${data.downtimeSeconds || 0}s)`);
        await fetchStatus();
        setLoading(false);
    };

    const handleSweepMode = async (autoSweep) => {
        try {
            await apiFetch('/treasury/mode', {
                method: 'POST',
                body: JSON.stringify({ autoSweep }),
            });
            await fetchTreasury();
            setSweepMsg(`Mode set to ${autoSweep ? 'AUTO' : 'MANUAL'}`);
        } catch { setSweepMsg('Failed to set mode'); }
    };

    const handleManualSweep = async () => {
        if (!window.confirm('Trigger treasury sweep now? This will move excess SOL to cold wallets.')) return;
        setSweepLoading(true);
        setSweepMsg('');
        try {
            const result = await apiFetch('/treasury/sweep', { method: 'POST' });
            if (result.swept === 0) {
                setSweepMsg(`Nothing to sweep — ${result.reason || 'balance below threshold'}`);
            } else if (result.success) {
                setSweepMsg(`✅ Swept ${result.swept?.toFixed(4)} SOL to cold wallets`);
            } else {
                setSweepMsg(`⚠️ Partial: ${result.swept?.toFixed(4)} SOL moved — check logs`);
            }
            await fetchTreasury();
            await fetchWallets();
        } catch {
            setSweepMsg('Sweep failed — check server logs');
        }
        setSweepLoading(false);
    };

    const isPaused = status?.paused;

    return (
        <div className="space-y-4">
            {/* Platform Status */}
            <div className={`rounded-lg p-4 border ${isPaused
                ? 'bg-red-500/5 border-red-500/30'
                : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full ${isPaused ? 'bg-red-500' : 'bg-emerald-500 animate-pulse'}`} />
                        <span className={`text-sm font-black uppercase tracking-wider ${isPaused ? 'text-red-400' : 'text-emerald-400'}`}>
                            {isPaused ? 'PLATFORM PAUSED' : 'PLATFORM LIVE'}
                        </span>
                    </div>
                    <span className="text-sm text-gray-500 font-mono">
                        Uptime: {status?.uptime ? formatUptime(status.uptime) : '—'}
                    </span>
                </div>
                {isPaused && status?.reason && (
                    <p className="text-xs text-red-300/70 font-mono mb-2">Reason: {status.reason}</p>
                )}
            </div>

            {/* Emergency Controls */}
            <div className="rounded-lg p-4 border border-white/10 bg-white/[0.02]">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-3">Emergency Controls</h3>
                {!isPaused ? (
                    <div className="space-y-3">
                        <input
                            type="text"
                            value={shutdownReason}
                            onChange={(e) => setShutdownReason(e.target.value)}
                            placeholder="Shutdown reason (optional)"
                            className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-xs text-white placeholder-gray-600 outline-none focus:border-red-500/50 font-mono"
                        />
                        <button
                            onClick={handleShutdown}
                            disabled={loading}
                            className="w-full py-3 rounded-lg font-black text-xs uppercase tracking-[0.15em] bg-red-600 hover:bg-red-500 text-white transition-all disabled:opacity-50"
                        >
                            {loading ? 'Processing...' : '🛑 Emergency Shutdown'}
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={handleResume}
                        disabled={loading}
                        className="w-full py-3 rounded-lg font-black text-xs uppercase tracking-[0.15em] bg-emerald-600 hover:bg-emerald-500 text-white transition-all disabled:opacity-50"
                    >
                        {loading ? 'Processing...' : '✅ Resume Platform'}
                    </button>
                )}
                {message && <p className="text-xs text-blue-400 mt-2 font-mono">{message}</p>}
            </div>

            {/* Wallet Balances */}
            <div className="rounded-lg p-4 border border-white/10 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400">House Wallets (Mainnet)</h3>
                    <button onClick={fetchWallets} className="text-xs text-blue-400 hover:text-blue-300">Refresh</button>
                </div>
                {wallets ? (
                    <div className="space-y-2">
                        {Object.entries(wallets).map(([name, w]) => (
                            <div key={name} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                <div>
                                    <span className="text-sm uppercase tracking-wider text-gray-500 font-bold">
                                        {name === 'house' ? 'House Hot Wallet' : 'Fee Wallet'}
                                    </span>
                                    <p className="text-sm text-blue-400 hover:text-blue-300 font-mono truncate max-w-[300px]">
                                        <a href={`https://solscan.io/account/${w.address}`} target="_blank" rel="noopener noreferrer">
                                            {w.address}
                                        </a>
                                    </p>
                                </div>
                                <span className="text-sm font-mono font-bold text-white">
                                    {w.balance?.toFixed(4)} SOL
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-gray-600">Loading wallets...</p>
                )}
            </div>

            {/* ── Treasury Sweep ── */}
            <div className="rounded-lg p-4 border border-amber-500/20 bg-amber-500/[0.03]">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-black uppercase tracking-[0.2em] text-amber-400">Treasury Sweep</h3>
                    {treasury?.lastSweepAt && (
                        <span className="text-[10px] text-gray-500 font-mono">
                            Last: {new Date(treasury.lastSweepAt).toLocaleString()}
                        </span>
                    )}
                </div>

                {/* Mode Toggle */}
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs text-gray-500 font-bold uppercase tracking-wider mr-1">Mode:</span>
                    <button
                        onClick={() => handleSweepMode(false)}
                        className={`px-4 py-1.5 rounded-l text-xs font-black uppercase tracking-widest transition-all border ${
                            !treasury?.autoSweepEnabled
                                ? 'bg-blue-600 border-blue-500 text-white'
                                : 'bg-white/[0.03] border-white/10 text-gray-500 hover:text-gray-300'
                        }`}
                    >
                        Manual
                    </button>
                    <button
                        onClick={() => handleSweepMode(true)}
                        className={`px-4 py-1.5 rounded-r text-xs font-black uppercase tracking-widest transition-all border ${
                            treasury?.autoSweepEnabled
                                ? 'bg-emerald-600 border-emerald-500 text-white'
                                : 'bg-white/[0.03] border-white/10 text-gray-500 hover:text-gray-300'
                        }`}
                    >
                        Auto
                    </button>
                    {treasury?.autoSweepEnabled && (
                        <span className="text-[10px] text-emerald-400 font-mono ml-2 animate-pulse">
                            ● every {treasury.sweepIntervalMin}m
                        </span>
                    )}
                </div>

                {/* Stats Row */}
                {treasury && (
                    <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                        <div className="bg-white/[0.03] rounded p-2 border border-white/5">
                            <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-0.5">Hot Balance</div>
                            <div className="text-sm font-mono font-bold text-white">
                                {treasury.hotBalance != null ? `${treasury.hotBalance.toFixed(4)} SOL` : '—'}
                            </div>
                        </div>
                        <div className="bg-white/[0.03] rounded p-2 border border-white/5">
                            <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-0.5">Keep Hot</div>
                            <div className="text-sm font-mono font-bold text-amber-400">
                                {treasury.keepHotSol} SOL
                            </div>
                        </div>
                        <div className="bg-white/[0.03] rounded p-2 border border-white/5">
                            <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-0.5">Threshold</div>
                            <div className="text-sm font-mono font-bold text-blue-400">
                                {treasury.sweepThresholdSol} SOL
                            </div>
                        </div>
                    </div>
                )}

                {/* Cold Wallet Destinations */}
                {treasury && (treasury.coldWalletA || treasury.coldWalletB) && (
                    <div className="mb-4 space-y-1">
                        {treasury.coldWalletA && (
                            <div className="flex justify-between text-xs font-mono text-gray-400">
                                <span className="text-gray-600">Cold A ({treasury.splitA}%)</span>
                                <span>{treasury.coldWalletA}</span>
                            </div>
                        )}
                        {treasury.coldWalletB && (
                            <div className="flex justify-between text-xs font-mono text-gray-400">
                                <span className="text-gray-600">Cold B ({treasury.splitB}%)</span>
                                <span>{treasury.coldWalletB}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Sweep Now Button */}
                <button
                    onClick={handleManualSweep}
                    disabled={sweepLoading}
                    className="w-full py-2.5 rounded-lg font-black text-xs uppercase tracking-[0.15em] bg-amber-600/80 hover:bg-amber-500 text-white transition-all disabled:opacity-50 border border-amber-500/30"
                >
                    {sweepLoading ? 'Sweeping...' : '⚡ Sweep Now'}
                </button>
                {sweepMsg && <p className="text-xs text-amber-300 mt-2 font-mono">{sweepMsg}</p>}
                {!treasury?.coldWalletA && (
                    <p className="text-[10px] text-yellow-600 mt-2 font-mono">
                        ⚠ Set TREASURY_COLD_WALLET_A in .env to enable sweeps
                    </p>
                )}
            </div>
        </div>
    );
};

function formatUptime(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default AdminControls;
